import React from 'react';
import { useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useExitImpersonationMutation } from 'store/services/admin/adminApi';
import { loginSuccess } from 'store/services/auth/authSlice';
import { adminUserDetailPath } from 'routes/paths';
import './ImpersonationBanner.css';

interface ImpersonationBannerProps {
  impersonatedUsername: string;
  targetUserId: number;
}

const ImpersonationBanner: React.FC<ImpersonationBannerProps> = ({
  impersonatedUsername,
  targetUserId
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [exitImpersonation, { isLoading }] = useExitImpersonationMutation();

  const handleExitImpersonation = async () => {
    try {
      // Call backend to log impersonation exit
      await exitImpersonation().unwrap();

      // Restore admin token from localStorage backup
      const adminToken = localStorage.getItem('adminTokenBackup');
      const adminUser = localStorage.getItem('adminUserBackup');

      if (adminToken && adminUser) {
        // Restore admin session
        dispatch(loginSuccess({
          token: adminToken,
          user: JSON.parse(adminUser),
          isImpersonating: false
        }));

        // Clean up impersonation data
        localStorage.removeItem('adminTokenBackup');
        localStorage.removeItem('adminUserBackup');
        localStorage.removeItem('isImpersonating');
        localStorage.removeItem('impersonatedUsername');
        localStorage.removeItem('impersonatedUserId');

        toast.success('Exited impersonation mode');

        // Navigate back to user detail page
        navigate(adminUserDetailPath(targetUserId));
      } else {
        // Fallback: redirect to admin panel if backup not found
        toast.warning('Admin session backup not found. Redirecting to login...');
        localStorage.clear();
        navigate('/welcome');
      }
    } catch (error: any) {
      console.error('Failed to exit impersonation:', error);

      // Even if API call fails, try to restore session
      const adminToken = localStorage.getItem('adminTokenBackup');
      const adminUser = localStorage.getItem('adminUserBackup');

      if (adminToken && adminUser) {
        dispatch(loginSuccess({
          token: adminToken,
          user: JSON.parse(adminUser),
          isImpersonating: false
        }));

        localStorage.removeItem('adminTokenBackup');
        localStorage.removeItem('adminUserBackup');
        localStorage.removeItem('isImpersonating');
        localStorage.removeItem('impersonatedUsername');
        localStorage.removeItem('impersonatedUserId');

        toast.warning('Session restored despite API error');
        navigate(adminUserDetailPath(targetUserId));
      } else {
        toast.error('Failed to exit impersonation. Please refresh and log in again.');
      }
    }
  };

  return (
    <div className="impersonation-banner">
      <div className="impersonation-banner-content">
        <div className="impersonation-banner-left">
          <span className="impersonation-icon">🎭</span>
          <span className="impersonation-text">
            <strong>IMPERSONATING:</strong> {impersonatedUsername}
          </span>
        </div>
        <div className="impersonation-banner-right">
          <span className="impersonation-warning">
            All actions are performed as this user
          </span>
          <button
            className="exit-impersonation-button"
            onClick={handleExitImpersonation}
            disabled={isLoading}
          >
            {isLoading ? 'Exiting...' : 'Exit Impersonation'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImpersonationBanner;
